import type {
  AnalyticsMetricKey,
  AnalyticsQueryResult,
  AnalyticsWidgetConfig,
} from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { DealAnalyticsPanel } from "@/components/analytics/deal-analytics-panel";

const METRIC_LABELS: Record<AnalyticsMetricKey, string> = {
  emails_sent: "Emails sent",
  emails_opened: "Emails opened",
  emails_clicked: "Emails clicked",
  emails_replied: "Emails replied",
  emails_bounced: "Emails bounced",
  calls_logged: "Calls logged",
  calls_connected: "Calls connected",
  sequence_enrollments: "Enrollments",
  tasks_completed: "Tasks completed",
  tasks_open: "Open tasks",
  meetings_booked: "Meetings booked",
  meetings_completed: "Meetings completed",
  meetings_no_show: "No-shows",
  deals_won: "Deals won",
  deals_created: "Deals created",
  deals_lost: "Deals lost",
  pipeline_weighted: "Weighted pipeline",
  pipeline_open: "Open pipeline",
};

function formatMetric(key: AnalyticsMetricKey, n: number): string {
  if (key.startsWith("pipeline_")) {
    if (!Number.isFinite(n) || n <= 0) return "—";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);
  }
  return String(Math.round(n));
}

export function KpiCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}

export function FunnelChart({
  metrics,
  totals,
}: {
  metrics: AnalyticsMetricKey[];
  totals: Partial<Record<AnalyticsMetricKey, number>>;
}) {
  const max = Math.max(...metrics.map((m) => totals[m] ?? 0), 1);
  return (
    <div className="space-y-3">
      {metrics.map((m) => {
        const v = totals[m] ?? 0;
        const pct = Math.round((v / max) * 100);
        return (
          <div key={m} className="flex items-center gap-3">
            <span className="w-36 truncate text-sm text-foreground">
              {METRIC_LABELS[m] ?? m}
            </span>
            <div className="flex-1">
              <div className="h-3 overflow-hidden rounded-full bg-muted/20">
                <div
                  className="h-full rounded-full bg-brand"
                  style={{ width: `${Math.max(pct, 2)}%` }}
                />
              </div>
            </div>
            <span className="w-12 text-right text-sm text-muted-foreground">
              {formatMetric(m, v)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function BarChart({
  result,
  metrics,
}: {
  result: AnalyticsQueryResult;
  metrics: AnalyticsMetricKey[];
}) {
  const rows = result.rows.filter((r) => r.key !== "_all" || result.rows.length === 1);
  const max = Math.max(
    1,
    ...rows.flatMap((r) => metrics.map((m) => r.values[m] ?? 0)),
  );
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No data in range.</p>;
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {metrics.map((m, i) => (
          <span key={m} className="flex items-center gap-1.5">
            <span
              className={cn(
                "inline-block h-2 w-2 rounded-sm",
                i === 0 ? "bg-brand" : "bg-sky-500",
              )}
            />
            {METRIC_LABELS[m] ?? m}
          </span>
        ))}
      </div>
      <div className="flex items-end gap-2" style={{ height: 140 }}>
        {rows.slice(-16).map((row) => (
          <div
            key={row.key}
            className="flex flex-1 flex-col items-center gap-1"
            title={row.label}
          >
            <div className="flex h-28 w-full items-end justify-center gap-0.5">
              {metrics.map((m, i) => {
                const v = row.values[m] ?? 0;
                const pct = Math.round((v / max) * 100);
                return (
                  <div
                    key={m}
                    className={cn(
                      "w-full max-w-[14px] rounded-sm",
                      i === 0 ? "bg-brand" : "bg-sky-500",
                    )}
                    style={{
                      height: `${Math.max(pct, v > 0 ? 4 : 0)}%`,
                      minHeight: v > 0 ? 4 : 0,
                    }}
                  />
                );
              })}
            </div>
            <span className="max-w-full truncate text-[10px] text-muted-foreground">
              {row.label.length > 8 ? row.label.slice(5) : row.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MetricsTable({
  result,
  metrics,
}: {
  result: AnalyticsQueryResult;
  metrics: AnalyticsMetricKey[];
}) {
  const rows = result.rows.filter((r) => r.key !== "_all");
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No rows to show.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-border text-xs uppercase text-muted-foreground">
            <th className="px-2 py-2 font-medium">Group</th>
            {metrics.map((m) => (
              <th key={m} className="px-2 py-2 font-medium">
                {METRIC_LABELS[m] ?? m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-border/60">
              <td className="px-2 py-2 text-foreground">{row.label}</td>
              {metrics.map((m) => (
                <td key={m} className="px-2 py-2 text-muted-foreground">
                  {formatMetric(m, row.values[m] ?? 0)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AnalyticsWidget({
  widget,
  result,
  loading,
}: {
  widget: AnalyticsWidgetConfig;
  result?: AnalyticsQueryResult;
  loading?: boolean;
}) {
  if (widget.kind === "deal_summary") {
    return <DealAnalyticsPanel />;
  }

  const metrics = widget.metrics ?? [];
  const totals = result?.totals ?? {};

  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {widget.title}
      </h3>
      {loading && !result ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : widget.kind === "kpi" ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {metrics.map((m) => (
            <KpiCard
              key={m}
              label={METRIC_LABELS[m] ?? m}
              value={formatMetric(m, totals[m] ?? 0)}
            />
          ))}
        </div>
      ) : widget.kind === "funnel" ? (
        <FunnelChart metrics={metrics} totals={totals} />
      ) : widget.kind === "bar" && result ? (
        <BarChart result={result} metrics={metrics} />
      ) : widget.kind === "table" && result ? (
        <MetricsTable result={result} metrics={metrics} />
      ) : (
        <p className="text-sm text-muted-foreground">No data.</p>
      )}
    </div>
  );
}

export function resultToCsv(result: AnalyticsQueryResult): string {
  const metrics = Object.keys(result.totals) as AnalyticsMetricKey[];
  const header = ["group", ...metrics].join(",");
  const lines = result.rows.map((r) =>
    [
      JSON.stringify(r.label),
      ...metrics.map((m) => String(r.values[m] ?? 0)),
    ].join(","),
  );
  return [header, ...lines].join("\n");
}

export { METRIC_LABELS, formatMetric };
